import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Camera, Upload, Trash2, Loader2, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { SectionLabel } from "./ui";

/** Read/write photo gallery for a customer's own ticket — mirrors the CRM's
 * TicketPhotoGallery but styled for the consumer app. */
export default function CustomerTicketPhotoGallery({ ticketId }) {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const reload = useCallback(async () => {
    if (!ticketId) return;
    try {
      const { data } = await api.get(`/service-tickets/${ticketId}/photos`);
      setPhotos(data || []);
    } catch (err) { /* silent — non-critical */ }
  }, [ticketId]);

  useEffect(() => { reload(); }, [reload]);

  async function onPick(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        await api.post(`/service-tickets/${ticketId}/photos`, form, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Added ${files.length} photo${files.length > 1 ? "s" : ""}`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onDelete(p) {
    try {
      await api.delete(`/service-tickets/${ticketId}/photos/${p.id}`);
      setPhotos((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  if (!photos.length && !uploading) {
    return (
      <div data-testid="customer-ticket-photos">
        <div className="flex items-center justify-between mb-2">
          <SectionLabel className="flex items-center gap-1.5"><Camera className="w-3 h-3" /> Photos</SectionLabel>
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="customer-ticket-photo-add"
            className="text-[11px] font-bold text-[#0D4721] inline-flex items-center gap-1">
            <Upload className="w-3 h-3" /> Add
          </button>
        </div>
        <div className="text-[11px] text-slate-400 border border-dashed border-slate-200 rounded-xl py-3 text-center">No photos attached yet.</div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={onPick} className="hidden" />
      </div>
    );
  }

  return (
    <div data-testid="customer-ticket-photos">
      <div className="flex items-center justify-between mb-2">
        <SectionLabel className="flex items-center gap-1.5"><Camera className="w-3 h-3" /> Photos · {photos.length}</SectionLabel>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="customer-ticket-photo-add"
          className="text-[11px] font-bold text-[#0D4721] inline-flex items-center gap-1 disabled:opacity-40">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Add
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={onPick} className="hidden" />
      <div className="grid grid-cols-4 gap-1.5">
        {photos.map((p) => (
          <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
            <button type="button" onClick={() => setLightbox(p)} className="block w-full h-full" data-testid={`customer-ticket-photo-thumb-${p.id}`}>
              <img src={`${backend}/api/files/${p.id}`} alt={p.filename} className="w-full h-full object-cover" loading="lazy" />
            </button>
            {p.uploaded_by_id === user?.id && (
              <button type="button" onClick={() => onDelete(p)} data-testid={`customer-ticket-photo-delete-${p.id}`}
                className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5">
                <Trash2 className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 text-white/80"><X className="w-6 h-6" /></button>
          <img src={`${backend}/api/files/${lightbox.id}`} alt={lightbox.filename} onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}
